import { Selector, State } from "@ngxs/store";
import { PartnerHttpService } from "@api/servies/partner-http.service";
import { LoadingState } from "@store/common/LoadingState";
import { finalize, MonoTypeOperatorFunction, of, switchMap, tap } from "rxjs";
import { Injectable } from "@angular/core";
import { Utils } from "@utils/Utils";
import { User } from "@api/models/User";
import { NgxsDataRepository } from "@angular-ru/ngxs/repositories";
import { DataAction, StateRepository } from "@angular-ru/ngxs/decorators";


export interface PartnerUserStateModel {
  item: User;
  parentId?: number,
  loading: LoadingState;
  newAvatar?: string;
}

export const PARTNER_USER_STATE_DEFAULT: PartnerUserStateModel = {
  item: new User(),
  loading: LoadingState.IDLE
}

@StateRepository()
@State<PartnerUserStateModel>({
  name: 'PartnerUser',
  defaults: PARTNER_USER_STATE_DEFAULT
})
@Injectable()
export class PartnerUserState extends NgxsDataRepository<PartnerUserStateModel> {

  constructor(private dataService: PartnerHttpService) {
    super();
  }

  @Selector()
  static item(state: PartnerUserStateModel) {
    return state.item
  }

  @Selector()
  static isLoading(state: PartnerUserStateModel) {
    return state.loading === LoadingState.LOADING;
  }

  @Selector()
  static isSaving(state: PartnerUserStateModel) {
    return state.loading === LoadingState.SAVING;
  }

  @Selector()
  static avatar(state: PartnerUserStateModel) {
    return state.newAvatar || state.item.imageUrl;
  }

  @DataAction({ cancelUncompleted: true })
  get(id: number, partnerId: number) {
    this.ctx.patchState({...this.initialState, loading: LoadingState.LOADING, parentId: partnerId})
    return this.dataService.getUser(id, partnerId).pipe(
      this.loadingIdleFinalize,
      this.requestSuccessTap,
    )
  }

  @DataAction()
  create(item: User, partnerId: number) {
    this.ctx.patchState({loading: LoadingState.SAVING, parentId: partnerId})
    return this.dataService.createUser(item, partnerId).pipe(
      this.loadingIdleFinalize,
      this.updateAvatarSwitchMap,
      this.requestSuccessTap,
    )
  }

  @DataAction()
  update(item: User, partnerId: number) {
    this.ctx.patchState({loading: LoadingState.SAVING})
    return this.dataService.updateUser(item, partnerId).pipe(
      this.loadingIdleFinalize,
      this.updateAvatarSwitchMap,
      this.requestSuccessTap,
    )
  }

  @DataAction()
  changeAvatar(avatar: string) {
    this.ctx.patchState({ newAvatar: avatar })
  }

  @DataAction()
  uploadAvatar(avatar: string) {
    this.ctx.patchState({newAvatar: avatar})
    const state = this.ctx.getState()
    const formData = new FormData()
    formData.append('file', Utils.dataURItoBlob(avatar))
    return this.dataService.updateUserImage(state.item.id as number, state.parentId as number, formData).pipe(this.requestSuccessTap)
  }

  private requestSuccessTap: MonoTypeOperatorFunction<User> = tap((item: User) => this.patchState({item, newAvatar: undefined}))

  private loadingIdleFinalize: MonoTypeOperatorFunction<User> = finalize(() => { this.patchState({loading: LoadingState.IDLE})})

  private updateAvatarSwitchMap: MonoTypeOperatorFunction<User> = switchMap((item: User) => {
    const state = this.ctx.getState()
    if (state.newAvatar) {
      const formData = new FormData()
      formData.append('file', Utils.dataURItoBlob(state.newAvatar))
      return this.dataService.updateUserImage(item.id as number, state.parentId as number, formData)
    } else {
      return of(item)
    }
  })
}
